import { getActiveOrganizationId } from 'utils/activeOrganization';
/**
 * Singleton WebSocket manager to ensure only one WebSocket connection
 */
class WebSocketManager {
    // eslint-disable-next-line no-empty-function
    constructor() {
        this.ws = null;
        this.subscribers = new Map();
        this.isConnected = false;
        this.isConnecting = false;
        this.reconnectTimeout = null;
    }
    static getInstance() {
        if (!WebSocketManager.instance) {
            WebSocketManager.instance = new WebSocketManager();
        }
        return WebSocketManager.instance;
    }
    static getWebSocketUrl() {
        const httpUrl = process.env.GRAPHQL_URI || '';
        if (!httpUrl) {
            return '';
        }
        // Convert HTTP GraphQL URL to WebSocket URL
        // e.g., http://localhost:8000/graphql -> ws://localhost:8000/subscriptions/websocket
        return httpUrl.replace(/\/graphql\/?$/, '/subscriptions/websocket').replace(/^http/, 'ws');
    }
    connect() {
        var _a, _b;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            return this.ws;
        }
        if (this.isConnecting) {
            return this.ws;
        }
        if (((_b = this.ws) === null || _b === void 0 ? void 0 : _b.readyState) === WebSocket.CLOSED) {
            this.ws = null;
        }
        const wsUrl = WebSocketManager.getWebSocketUrl();
        if (!wsUrl) {
            return null;
        }
        this.isConnecting = true;
        const ws = new WebSocket(wsUrl);
        this.ws = ws;
        ws.onopen = () => {
            this.isConnecting = false;
            this.isConnected = true;
            // Sent before subscribers are notified: the backend refuses any subscription on a
            // connection that has not been initialised. The organization pins the connection to that
            // organization's data, like the X-Organization-Id header does for HTTP requests.
            const token = localStorage.getItem('token');
            if (token) {
                const organizationId = getActiveOrganizationId();
                ws.send(JSON.stringify({
                    type: 'connection_init',
                    payload: Object.assign({ authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` }, (organizationId ? { organizationId } : {})),
                }));
            }
            this.subscribers.forEach((subscriber) => {
                subscriber.onConnectionChange(true);
            });
        };
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.subscribers.forEach((subscriber) => {
                    subscriber.onMessage(data);
                });
            }
            catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };
        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.isConnecting = false;
            this.isConnected = false;
        };
        ws.onclose = (event) => {
            this.isConnecting = false;
            this.isConnected = false;
            this.subscribers.forEach((subscriber) => {
                subscriber.onConnectionChange(false);
            });
            const shouldReconnect = this.subscribers.size > 0 && (event.code !== 1000 || event.reason === 'Token refresh');
            if (shouldReconnect) {
                if (this.reconnectTimeout) {
                    clearTimeout(this.reconnectTimeout);
                }
                this.reconnectTimeout = setTimeout(() => {
                    this.ws = null;
                    this.connect();
                }, 3000);
            }
            else {
                this.ws = null;
            }
        };
        return ws;
    }
    subscribe(subscriber) {
        this.subscribers.set(subscriber.id, subscriber);
        if (!this.ws ||
            (this.ws.readyState !== WebSocket.OPEN && this.ws.readyState !== WebSocket.CONNECTING)) {
            this.connect();
        }
        if (this.isConnected) {
            subscriber.onConnectionChange(true);
        }
    }
    unsubscribe(subscriberId) {
        this.subscribers.delete(subscriberId);
        if (this.subscribers.size === 0 && this.ws) {
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
            this.ws.close(1000, 'No more subscribers');
            this.ws = null;
            this.isConnected = false;
        }
    }
    sendMessage(message) {
        var _a;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
        else {
            console.error('Cannot send message: WebSocket not connected');
        }
    }
    getConnectionState() {
        return this.isConnected;
    }
    /**
     * Disconnect WebSocket and allow automatic reconnection.
     * Used when token is refreshed to reconnect with new token.
     */
    disconnect() {
        if (this.ws) {
            // Close with code 1000 (normal closure) to trigger reconnect
            // The onclose handler will trigger reconnection if subscribers exist
            this.ws.close(1000, 'Token refresh');
        }
    }
    cleanup() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.subscribers.clear();
        this.isConnected = false;
        this.isConnecting = false;
    }
}
export default WebSocketManager;
